import { contrastRatio as getContrastRatio } from "toolbox/Colors";

// Kept identical to the Admin app's `modules/loyaltyProgram/tierBadgePalette.ts` (only the contrast import
// differs), so a tier's badge looks the same to staff and to patients.

/** Every colour a tier badge draws, derived from the tier's one colour. */
export type TierBadgeColors = {
  /** The brightest facets, turned toward the light. */
  readonly rimLight: string;
  /** The darkest facets, turned away from it. */
  readonly rimShade: string;
  /** The emblem's outline. */
  readonly edge: string;
  /** Facet tones between those: lit, the tier colour itself, then shaded. */
  readonly faceLight: string;
  readonly face: string;
  readonly faceShade: string;
  /** Highlight edges. */
  readonly ring: string;
  /** The pearl's specular lustre. */
  readonly glint: string;
  /** The engraved initial. */
  readonly emblem: string;
  /** The initial's offset copy: a shadow under a light letter, a highlight under a dark one. */
  readonly emblemDepth: string;
  /** Whether the tier colour is light enough to need a dark initial. */
  readonly isLightFace: boolean;
};

const WHITE = "#FFFFFF";
const BLACK = "#000000";
/** The dark ink the emblem contrast is weighed against, as the tier chips use. */
const DARK_INK = "#111827";
const HEX = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;

const channels = (hex: string): [number, number, number] => {
  const match = HEX.exec(hex);
  if (!match) throw new Error(`"${hex}" is not a #RRGGBB colour`);
  return [parseInt(match[1], 16), parseInt(match[2], 16), parseInt(match[3], 16)];
};

const toHex = (value: number) => Math.round(value).toString(16).padStart(2, "0").toUpperCase();

/** `from` moved `amount` (0 to 1) of the way to `to`, in sRGB. */
export const mixHex = (from: string, to: string, amount: number) => {
  const start = channels(from);
  const end = channels(to);
  return `#${start.map((channel, index) => toHex(channel + (end[index] - channel) * amount)).join("")}`;
};

/**
 * The badge's colours from the tier colour. Light comes from the top left: facets run from a lifted
 * tint to a deepened shade of the same hue, so a gold reads as metal and a navy as a gem. The
 * initial takes whichever of white or near-black has the higher WCAG contrast on the tier colour,
 * then is tinted toward it so it sits in the metal rather than on it.
 */
export const deriveTierBadgeColors = (colorHex: string): TierBadgeColors => {
  const isLightFace = getContrastRatio(colorHex, DARK_INK) > getContrastRatio(colorHex, WHITE);

  return {
    rimLight: mixHex(colorHex, WHITE, 0.62),
    rimShade: mixHex(colorHex, BLACK, 0.42),
    edge: mixHex(colorHex, BLACK, isLightFace ? 0.56 : 0.6),
    faceLight: mixHex(colorHex, WHITE, isLightFace ? 0.5 : 0.34),
    face: colorHex.toUpperCase(),
    faceShade: mixHex(colorHex, BLACK, isLightFace ? 0.2 : 0.3),
    ring: mixHex(colorHex, WHITE, 0.72),
    glint: mixHex(colorHex, WHITE, 0.9),
    emblem: isLightFace ? mixHex(colorHex, BLACK, 0.74) : mixHex(colorHex, WHITE, 0.94),
    emblemDepth: isLightFace ? mixHex(colorHex, WHITE, 0.7) : mixHex(colorHex, BLACK, 0.62),
    isLightFace,
  };
};
